<template>
  <nav
    class="pointer-events-none fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-[45] hidden max-w-[calc(100vw-1.5rem)] -translate-x-1/2 sm:block"
    aria-label="Acciones rápidas"
    @pointermove="onPointerMove"
    @pointerleave="resetDock"
  >
    <div
      class="pointer-events-auto flex max-w-[calc(100vw-1.5rem)] items-center justify-center gap-1 rounded-2xl border border-white/10 bg-[#0a0e1a]/75 p-1.5 shadow-[0_0.75rem_2rem_rgba(0,0,0,0.42)] backdrop-blur-xl backdrop-saturate-150 max-[520px]:max-w-[calc(100vw-1rem)] max-[520px]:gap-0.5"
    >
      <router-link
        :ref="(el) => setItemRef(el?.$el || el, 0)"
        :to="panelItem.to"
        :class="itemClasses(panelItem)"
        :style="itemStyle(0, panelItem)"
        :aria-label="panelItem.title"
        :title="panelItem.title"
      >
        <span
          class="inline-flex flex-none origin-center scale-[var(--qa-icon-scale)] transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:scale-100 motion-reduce:transition-none"
        >
          <IconFbochas :name="panelItem.icon" :size="24" />
        </span>
        <span
          class="inline-block min-w-0 max-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[0.74rem] font-bold leading-none text-white opacity-0 transition-[max-width,opacity] delay-75 duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:max-w-[7.5rem] group-hover:opacity-100 group-focus-visible:max-w-[7.5rem] group-focus-visible:opacity-100 max-[520px]:text-[0.7rem] motion-reduce:transition-none"
        >
          {{ panelItem.shortTitle }}
        </span>
      </router-link>

      <span
        v-if="showDivider"
        class="mb-1 h-[1.55rem] w-px flex-none bg-white/15 max-[520px]:h-[1.45rem]"
        aria-hidden="true"
      ></span>

      <router-link
        v-for="(item, index) in dockItems"
        :key="item.key"
        :ref="(el) => setItemRef(el?.$el || el, index + 1)"
        :to="item.to"
        :class="itemClasses(item)"
        :style="itemStyle(index + 1, item)"
        :aria-label="item.title"
        :title="item.title"
      >
        <span
          class="inline-flex flex-none origin-center scale-[var(--qa-icon-scale)] transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:scale-100 motion-reduce:transition-none"
        >
          <IconFbochas :name="item.icon" :size="24" />
        </span>
        <span
          class="inline-block min-w-0 max-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[0.74rem] font-bold leading-none text-white opacity-0 transition-[max-width,opacity] delay-75 duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:max-w-[7.5rem] group-hover:opacity-100 group-focus-visible:max-w-[7.5rem] group-focus-visible:opacity-100 max-[520px]:text-[0.7rem] motion-reduce:transition-none"
        >
          {{ item.shortTitle }}
        </span>
      </router-link>

      <template v-if="loading">
        <span
          v-for="i in 3"
          :key="`skel-${i}`"
          class="h-9 w-9 flex-none animate-pulse rounded-2xl border border-white/10 bg-white/[0.08] motion-reduce:animate-none"
          aria-hidden="true"
        ></span>
      </template>
    </div>
  </nav>

  <nav
    class="fixed inset-x-0 bottom-0 z-[45] border-t border-white/10 bg-[#0a0e1a]/90 px-4 pb-[max(0.6rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_-0.75rem_2rem_rgba(0,0,0,0.32)] backdrop-blur-xl sm:hidden"
    aria-label="Navegación admin mobile"
  >
    <div class="mx-auto grid max-w-sm grid-cols-2 gap-2">
      <router-link
        to="/admin"
        :class="mobileNavItemClasses(panelItem)"
        aria-label="Ir al panel"
      >
        <IconFbochas name="home" :size="20" />
        <span class="text-[0.68rem] font-bold leading-none">Panel</span>
      </router-link>

      <button
        type="button"
        class="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.08] text-sm font-bold text-white/80 transition-colors duration-200 hover:bg-white/[0.12] focus:outline-none focus-visible:border-bochas-rojo/70"
        aria-haspopup="dialog"
        :aria-expanded="sheetOpen"
        @click="sheetOpen = true"
      >
        <IconFbochas name="categories" :size="20" />
        <span>Menú</span>
      </button>
    </div>
  </nav>

  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)]"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-out"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="sheetOpen"
        class="fixed inset-0 z-50 sm:hidden"
        :class="
          sheetDragging
            ? ''
            : 'transition-[background-color,backdrop-filter] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
        "
        :style="sheetOverlayStyle"
        @click.self="closeSheet"
      >
        <Transition
          appear
          enter-active-class="transition duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          enter-from-class="translate-y-full opacity-80"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="translate-y-0 opacity-100"
          leave-to-class="translate-y-full opacity-80"
        >
          <section
            class="absolute inset-x-0 bottom-0 max-h-[92svh] overflow-hidden rounded-t-3xl border border-white/10 bg-[#111827] pb-[max(1rem,env(safe-area-inset-bottom))] text-white shadow-2xl"
            :class="
              sheetDragging
                ? ''
                : 'transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
            "
            :style="sheetDragStyle"
            role="dialog"
            aria-modal="true"
            aria-label="Accesos de administración"
            @pointermove="moveSheetDrag"
            @pointerup="endSheetDrag"
            @pointercancel="cancelSheetDrag"
          >
            <div
              class="cursor-grab select-none touch-none active:cursor-grabbing"
              @pointerdown="startSheetDrag"
            >
              <div
                class="mx-auto mt-3 h-1.5 w-11 rounded-full bg-white/25"
              ></div>

              <div class="flex items-center justify-between gap-3 px-5 py-4">
                <div>
                  <p class="text-xs font-semibold uppercase text-bochas-rojo">
                    Accesos
                  </p>
                  <h2 class="text-lg font-bold">Panel administrativo</h2>
                </div>
                <button
                  type="button"
                  class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.08] text-white/80 transition-colors hover:bg-white/[0.12]"
                  aria-label="Cerrar accesos"
                  @click.stop="closeSheet"
                >
                  <IconFbochas name="chevron-down" :size="20" />
                </button>
              </div>
            </div>

            <div class="max-h-[calc(92svh-6.5rem)] overflow-y-auto px-4 pb-4">
              <div
                class="mb-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-bochas-rojo/30 text-sm font-black text-white"
                  >
                    {{ userInitials }}
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-semibold uppercase text-white/40">
                      Tu cuenta
                    </p>
                    <p class="mt-1 truncate text-base font-bold text-white">
                      {{ userName }}
                    </p>
                    <p class="mt-0.5 break-all text-xs text-white/50">
                      {{ userEmail }}
                    </p>
                  </div>
                </div>
                <div
                  class="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/55"
                >
                  <span
                    class="rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1"
                  >
                    {{ userRoleLabel }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-emerald-200"
                  >
                    <span
                      class="h-1.5 w-1.5 rounded-full bg-emerald-300"
                    ></span>
                    Sesión activa
                  </span>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <router-link
                  v-for="item in mobileActions"
                  :key="item.to"
                  :to="item.to"
                  :class="mobileSheetItemClasses(item)"
                  @click="closeSheet"
                >
                  <span
                    class="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/[0.08] text-white"
                  >
                    <IconFbochas :name="item.icon" :size="20" />
                  </span>
                  <span class="min-w-0">
                    <span class="block truncate text-sm font-bold">
                      {{ item.title }}
                    </span>
                    <span
                      class="mt-0.5 block line-clamp-2 text-[0.68rem] leading-snug text-white/50"
                    >
                      {{ item.description }}
                    </span>
                  </span>
                </router-link>
              </div>

              <div v-if="dockItems.length" class="mt-5">
                <p
                  class="mb-2 px-1 text-xs font-semibold uppercase text-white/40"
                >
                  Recientes
                </p>
                <div class="space-y-2">
                  <router-link
                    v-for="item in dockItems"
                    :key="`recent-${item.key}`"
                    :to="item.to"
                    class="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-3 text-white/80 transition-colors hover:bg-white/[0.1]"
                    @click="closeSheet"
                  >
                    <IconFbochas :name="item.icon" :size="19" />
                    <span class="truncate text-sm font-semibold">
                      {{ item.shortTitle }}
                    </span>
                  </router-link>
                </div>
              </div>
            </div>
          </section>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { getStoredUser } from "@/services/auth/authService";
import { getRecentActivity } from "@/services/activity/activityService";
import { ROLES, hasAnyRole, isGeneralAdmin, roleLabel } from "@/utils/roles";
import {
  crearAdminAccess,
  obtenerAdminAccess,
  obtenerModuloAdminPorRuta,
  puedeVerModulo,
} from "@/services/admin/adminModulesService";

const route = useRoute();
const RECENT_ACCESS_KEY = "bochas-admin-recent-accesses";
const MAX_RECENT_ACCESSES = 3;
const user = ref(getStoredUser());
const recentItems = ref([]);
const recentAccesses = ref([]);
const loading = ref(true);
const hasLoaded = ref(false);
const mouseX = ref(null);
const itemRefs = ref([]);
const sheetOpen = ref(false);
const sheetDragging = ref(false);
const sheetDragStartY = ref(0);
const sheetDragY = ref(0);
const adminAccess = ref(crearAdminAccess());

const typeIcons = {
  news: "newspaper",
  noticia: "newspaper",
  historia: "doc",
  history: "doc",
  nota: "edit",
  note: "edit",
  calendar: "calendar",
  event: "calendar",
  resultado: "trophy",
  result: "trophy",
  asociación: "location",
  association: "location",
  federación: "shield",
  club: "users",
  user: "users",
};

const panelItem = {
  key: "panel",
  title: "Panel",
  shortTitle: "Panel",
  icon: "home",
  to: "/admin",
  exact: true,
};

const allMobileActions = [
  {
    title: "Panel",
    description: "Volver al inicio del admin.",
    icon: "home",
    to: "/admin",
    exact: true,
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN],
  },
  {
    title: "Noticias",
    description: "Novedades y comunicados.",
    icon: "newspaper",
    to: "/admin/noticias",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN],
  },
  {
    title: "Historias",
    description: "Historias destacadas.",
    icon: "doc",
    to: "/admin/historias",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    title: "Notas",
    description: "Apuntes editoriales.",
    icon: "edit",
    to: "/admin/notas",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
    hidden: true,
  },
  {
    title: "Calendario",
    description: "Próximas fechas.",
    icon: "calendarClock",
    to: "/admin/calendario",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN],
  },
  {
    title: "Resultados",
    description: "Podios jugados.",
    icon: "trophy",
    to: "/admin/resultados",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    title: "Pasado/Futuro",
    description: "Jugadores Históricos y Jóvenes Bochófilos.",
    icon: "hourglass",
    to: "/admin/pasado-futuro",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    title: "Asociaciones",
    description: "Instituciones regionales.",
    icon: "location",
    to: "/admin/asociaciones",
    roles: [ROLES.BOCHAS_ADMIN],
  },
  {
    title: "Alojamiento",
    description: "Textos, imágenes, comodidades y tarifas.",
    to: "/admin/alojamiento",
    icon: "alojamiento",
    iconClass: "admin-action-card__icon--blue",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    title: "Portadas",
    description: "Imágenes del sitio.",
    icon: "camara",
    to: "/admin/portadas",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    title: "Configuración",
    description: "Redes y SEO.",
    icon: "settings",
    to: "/admin/configuracion",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    title: "La Federación",
    description: "Autoridades y documentos.",
    icon: "shield",
    to: "/admin/federacion",
    roles: [ROLES.BOCHAS_ADMIN],
    requiresGeneralAdmin: true,
  },
  {
    title: "Clubes",
    description: "Clubes afiliados.",
    icon: "users",
    to: "/admin/clubes",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
  },
  {
    title: "Mi Club",
    description: "Editar tu club.",
    icon: "casa",
    to: "/admin/mi-club",
    roles: [ROLES.CLUB_ADMIN],
  },
  {
    title: "Usuarios",
    description: "Accesos internos.",
    icon: "users",
    to: "/admin/usuarios",
    roles: [ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN],
    requiresGeneralAdmin: true,
  },
];

const dockItems = computed(() => {
  const items = recentAccesses.value.length
    ? recentAccesses.value
    : recentItems.value.map(normalizeActivityItem);
  return items.filter((item) => {
    const module = obtenerModuloAdminPorRuta(item.to);
    return !module || puedeVerModulo(adminAccess.value, module, user.value);
  });
});
const mobileActions = computed(() =>
  allMobileActions.filter(
    (item) =>
      !item.hidden &&
      hasAnyRole(user.value, ...item.roles) &&
      (!item.requiresGeneralAdmin || isGeneralAdmin(user.value)) &&
      puedeVerModulo(
        adminAccess.value,
        obtenerModuloAdminPorRuta(item.to),
        user.value,
      ),
  ),
);
const showDivider = computed(
  () => recentItems.value.length > 0 || loading.value,
);
const userName = computed(() => {
  if (!user.value) return "Administrador";
  const fullName = [user.value.firstname, user.value.lastname]
    .filter(Boolean)
    .join(" ");
  return fullName || "Administrador";
});
const userEmail = computed(() => user.value?.email || "Sin email cargado");
const userRoleLabel = computed(() => roleLabel(user.value?.role, user.value));
const userInitials = computed(() => {
  const initials = userName.value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return initials || "A";
});
const sheetDragStyle = computed(() => {
  if (!sheetDragging.value && sheetDragY.value === 0) return null;
  return {
    transform: `translateY(${sheetDragY.value}px)`,
  };
});

const sheetOverlayStyle = computed(() => {
  const progress = Math.min(sheetDragY.value / 160, 1);
  const opacity = 0.45 * (1 - progress);
  const blur = 4 * (1 - progress);

  return {
    backgroundColor: `rgba(0, 0, 0, ${opacity})`,
    backdropFilter: `blur(${blur}px)`,
  };
});

function normalizeActivityItem(item, index) {
  const title = item.title || item.shortTitle || "Acción reciente";
  const type = String(item.type || "").toLowerCase();

  return {
    key: item.key || `${item.type || "activity"}-${item.id || index}`,
    title,
    shortTitle: item.shortTitle || title,
    icon: item.icon || typeIcons[type] || "doc",
    to: item.to || item.link || item.href || "/admin",
    exact: false,
  };
}

function setItemRef(el, index) {
  if (el) itemRefs.value[index] = el;
}

function onPointerMove(event) {
  if (event.pointerType === "touch") return;
  mouseX.value = event.clientX;
}

function resetDock() {
  mouseX.value = null;
}

function influenceFor(index) {
  if (mouseX.value === null) return 0;
  const el = itemRefs.value[index];
  if (!el) return 0;

  const rect = el.getBoundingClientRect();
  const center = rect.left + rect.width / 2;
  const distance = Math.abs(mouseX.value - center);
  const radius = 156;
  return Math.max(0, 1 - distance / radius);
}

function itemStyle(index, item) {
  const influence = influenceFor(index);
  const size = 46 + influence * 3.5;
  const lift = influence * -1;
  const iconScale = 1 + influence * 0.03;
  const titleLength = (item?.shortTitle || item?.title || "").length;
  const expandedSize = Math.min(148, Math.max(104, 58 + titleLength * 7));

  return {
    "--qa-size": `${size}px`,
    "--qa-expanded-size": `${expandedSize}px`,
    "--qa-lift": `${lift}px`,
    "--qa-icon-scale": iconScale,
  };
}

function itemClasses(item) {
  return [
    "group relative inline-flex h-[var(--qa-size)] w-[var(--qa-size)] max-w-[11rem] flex-none translate-y-[var(--qa-lift)] transform-gpu items-center justify-center gap-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.08] text-white/70 no-underline outline-none transition-[width,height,transform,background-color,border-color,color,box-shadow,padding,gap] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] hover:w-[var(--qa-expanded-size)] hover:min-w-[var(--qa-size)] hover:justify-start hover:gap-2 hover:border-white/25 hover:bg-white/[0.14] hover:px-3 hover:pl-2.5 hover:text-white hover:shadow-[0_0.75rem_1.5rem_rgba(0,0,0,0.28)] focus-visible:w-[var(--qa-expanded-size)] focus-visible:min-w-[var(--qa-size)] focus-visible:justify-start focus-visible:gap-2 focus-visible:border-white/25 focus-visible:bg-white/[0.14] focus-visible:px-3 focus-visible:pl-2.5 focus-visible:text-white focus-visible:shadow-[0_0.75rem_1.5rem_rgba(0,0,0,0.28)] max-[520px]:!h-9 max-[520px]:!w-9 max-[520px]:!translate-y-0 max-[520px]:hover:!w-[var(--qa-expanded-size)] max-[520px]:focus-visible:!w-[var(--qa-expanded-size)] motion-reduce:!h-9 motion-reduce:!w-9 motion-reduce:!translate-y-0 motion-reduce:transition-none",
    item.key === "panel" &&
      "border-bochas-rojo/50 bg-bochas-rojo/30 text-white hover:border-bochas-rojo/80 hover:bg-bochas-rojo/50 focus-visible:border-bochas-rojo/80 focus-visible:bg-bochas-rojo/50",
    isActive(item) && "border-bochas-rojo/80 bg-bochas-rojo/50 text-white",
  ];
}

function mobileNavItemClasses(item) {
  return [
    "inline-flex h-12 items-center justify-center gap-2 rounded-2xl border text-sm font-bold transition-colors duration-200",
    isActive(item)
      ? "border-bochas-rojo/70 bg-bochas-rojo/35 text-white"
      : "border-white/10 bg-white/[0.08] text-white/80 hover:bg-white/[0.12]",
  ];
}

function mobileSheetItemClasses(item) {
  return [
    "flex min-h-[5.2rem] flex-col justify-between rounded-2xl border p-3 text-left transition-colors duration-200",
    isActive(item)
      ? "border-bochas-rojo/70 bg-bochas-rojo/25 text-white"
      : "border-white/10 bg-white/[0.06] text-white/80 hover:bg-white/[0.1]",
  ];
}

function isActive(item) {
  if (item.exact) return route.path === item.to;
  return route.path === item.to || route.path.startsWith(`${item.to}/`);
}

function closeSheet() {
  resetSheetDrag();
  sheetOpen.value = false;
}

function startSheetDrag(event) {
  if (event.pointerType === "mouse" && event.button !== 0) return;
  sheetDragging.value = true;
  sheetDragStartY.value = event.clientY;
  sheetDragY.value = 0;
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function moveSheetDrag(event) {
  if (!sheetDragging.value) return;
  const distance = event.clientY - sheetDragStartY.value;
  sheetDragY.value = Math.max(0, distance);
}

function endSheetDrag() {
  if (!sheetDragging.value) return;

  sheetDragging.value = false;
  if (sheetDragY.value > 96) {
    sheetDragY.value = window.innerHeight;
    window.setTimeout(() => {
      sheetOpen.value = false;
      resetSheetDrag();
    }, 180);
    return;
  }

  sheetDragY.value = 0;
}

function cancelSheetDrag() {
  sheetDragging.value = false;
  sheetDragY.value = 0;
}

function resetSheetDrag() {
  sheetDragging.value = false;
  sheetDragStartY.value = 0;
  sheetDragY.value = 0;
}

function loadRecentAccesses() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_ACCESS_KEY) || "[]");
    recentAccesses.value = Array.isArray(stored)
      ? stored.slice(0, MAX_RECENT_ACCESSES)
      : [];
  } catch {
    recentAccesses.value = [];
  }
}

function saveRecentAccesses(items) {
  recentAccesses.value = items.slice(0, MAX_RECENT_ACCESSES);
  localStorage.setItem(RECENT_ACCESS_KEY, JSON.stringify(recentAccesses.value));
}

function findCurrentAccess() {
  const available = allMobileActions
    .filter((item) => item.key !== "panel")
    .filter((item) => hasAnyRole(user.value, ...item.roles))
    .filter((item) => !item.requiresGeneralAdmin || isGeneralAdmin(user.value))
    .filter((item) =>
      puedeVerModulo(
        adminAccess.value,
        obtenerModuloAdminPorRuta(item.to),
        user.value,
      ),
    )
    .filter(
      (item) =>
        route.path === item.to ||
        (!item.exact && route.path.startsWith(`${item.to}/`)),
    )
    .sort((a, b) => b.to.length - a.to.length);

  const item = available[0];
  if (!item || item.to === panelItem.to) return null;

  return {
    key: item.to,
    title: item.title,
    shortTitle: item.title,
    icon: item.icon,
    to: item.to,
    exact: item.exact,
    visitedAt: Date.now(),
  };
}

function rememberCurrentAccess() {
  const item = findCurrentAccess();
  if (!item) return;

  const next = [
    item,
    ...recentAccesses.value.filter((recent) => recent.to !== item.to),
  ];
  saveRecentAccesses(next);
}

async function load() {
  loading.value = !hasLoaded.value;
  try {
    const [data, savedAdminAccess] = await Promise.all([
      getRecentActivity(),
      obtenerAdminAccess(),
    ]);
    adminAccess.value = savedAdminAccess;
    recentItems.value = (data.items || []).filter(Boolean).slice(0, 5);
  } catch {
    recentItems.value = [];
  } finally {
    hasLoaded.value = true;
    loading.value = false;
  }
}

loadRecentAccesses();

watch(
  () => route.fullPath,
  () => {
    closeSheet();
    if (route.path.startsWith("/admin")) {
      rememberCurrentAccess();
      load();
    }
  },
  { immediate: true },
);
</script>
